/**
 * Accordion Component
 * Reusable collapsible accordion sections with smooth animations
 * Supports multiple open accordions simultaneously
 * Supports Next button functionality for sequential navigation
 * Supports completion state with green border and tick icon
 */

import { AlertCircle, Check, Pencil } from "lucide-react"
import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { Button } from "@/components/ui/button"

export interface AccordionItem {
  id: string
  title: string
  content: React.ReactNode
  showNextButton?: boolean // Show Next button at bottom of content
  isComplete?: boolean // Show completion state with green border and tick
  hasError?: boolean // Show error state with red border and exclamation icon
  summary?: string // Summary text to show when complete and collapsed
  price?: string | number // Price to display before summary
  displayName?: string // Custom display name for title when complete and closed
  userTitle?: string // User-provided title to display in collapsed view (e.g., from Info section)
  userPreviews?: Array<{
    userId: string
    name: string | null
    email: string
    username: string | null
    profilePictureUrl: string | null
  }>
  totalCount?: number // Total count for users section (e.g., for calculating "+X more")
  showViewMore?: boolean // Show "View More" button when there are more items than visible
  error?: string // Error message to display when hasError is true and collapsed
}

export interface AccordionRef {
  openFirstAccordion: () => void
}

export interface AccordionProps {
  items: AccordionItem[]
  defaultOpenIndex?: number // First item (0) by default
  allowMultiple?: boolean // Default: true (multiple can be open)
  onNextClick?: (currentIndex: number) => boolean | Promise<boolean> // Callback when Next button clicked, return false to prevent navigation
  onItemClose?: (closedItemId: string) => void // Callback when an accordion item is closed
  onOpenChange?: (openItems: Set<string>) => void // Callback when open items change
  onSectionAttempt?: (sectionId: string) => void // Callback when Next button is clicked (regardless of validation result)
  accordionItemRef?: (id: string) => (element: HTMLDivElement | null) => void // Callback to get ref for each accordion item
  attemptedSections?: Set<string> // Sections that have been opened - errors only show after section has been attempted
  className?: string
}

export const Accordion = forwardRef<AccordionRef, AccordionProps>(
  (
    {
      items,
      defaultOpenIndex = 0,
      allowMultiple = true,
      onNextClick,
      onItemClose,
      onOpenChange,
      onSectionAttempt,
      accordionItemRef,
      attemptedSections,
      className = "",
    },
    ref,
  ) => {
    // Initialize with defaultOpenIndex open
    const [openItems, setOpenItems] = useState<Set<string>>(
      new Set(defaultOpenIndex !== undefined ? [items[defaultOpenIndex]?.id].filter(Boolean) : []),
    )

    // Notify parent when open items change
    useEffect(() => {
      onOpenChange?.(openItems)
    }, [openItems, onOpenChange])

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      openFirstAccordion: () => {
        if (items.length > 0) {
          setOpenItems(new Set([items[0].id]))
        }
      },
    }))

    const toggleItem = (id: string) => {
      setOpenItems(prev => {
        const newOpen = new Set(prev)

        if (newOpen.has(id)) {
          // Item is being closed
          newOpen.delete(id)
          // Call onItemClose callback if provided
          onItemClose?.(id)
        } else {
          if (allowMultiple) {
            newOpen.add(id)
          } else {
            // If only one can be open, close all others and open this one
            newOpen.clear()
            newOpen.add(id)
          }
        }

        return newOpen
      })
    }

    const handleKeyDown = (event: React.KeyboardEvent, id: string) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        toggleItem(id)
      } else if (event.key === "Escape") {
        // Close the currently focused item
        setOpenItems(prev => {
          const newOpen = new Set(prev)
          newOpen.delete(id)
          return newOpen
        })
      }
    }

    const handleNext = async (currentIndex: number) => {
      const currentItem = items[currentIndex]
      const nextItem = items[currentIndex + 1]

      // Notify parent that this section was attempted (Next button clicked)
      // This fires regardless of whether validation passes or fails
      onSectionAttempt?.(currentItem.id)

      if (nextItem) {
        // Call parent callback FIRST to validate
        let canProceed = true
        if (onNextClick) {
          const result = await onNextClick(currentIndex)
          canProceed = result !== false // true if undefined or true
        }

        // Close current accordion regardless of validation result
        // This ensures error styling shows on the closed section
        setOpenItems(prev => {
          const newOpen = new Set(prev)
          newOpen.delete(items[currentIndex].id)
          // Only open next if validation passed
          if (canProceed) {
            newOpen.add(nextItem.id)
          }
          return newOpen
        })

        // If validation passed, focus first input in next accordion after animation
        if (canProceed) {
          setTimeout(() => {
            const content = document.getElementById(`accordion-content-${nextItem.id}`)
            const firstInput = content?.querySelector(
              "input:not([disabled]), select:not([disabled]), textarea:not([disabled])",
            ) as HTMLElement
            firstInput?.focus()
          }, 300)
        }
      }
    }

    return (
      <div className={`space-y-5 ${className}`}>
        {items.map((item, index) => {
          const isOpen = openItems.has(item.id)
          const isLastItem = index === items.length - 1
          const showNextButton = item.showNextButton && !isLastItem
          const isComplete = item.isComplete
          const hasError = item.hasError
          const hasBeenAttempted = attemptedSections?.has(item.id) ?? true
          return (
            <div
              key={item.id}
              ref={accordionItemRef?.(item.id)}
              data-accordion-id={item.id}
              className={`relative border rounded-[20px] transition-all duration-200 ${
                hasError && !isOpen && hasBeenAttempted
                  ? "border-1 border-brand-terracotta bg-brand-terracotta/10"
                  : isComplete && !isOpen
                    ? "border-1 border-[var(--color-brand-olive)] bg-[var(--color-brand-olive-alpha-40)]"
                    : "border-1 bg-card border-card"
              }`}
            >
              {/* Tick Icon or Error Icon - Bottom Right */}
              {!isOpen && (isComplete || (hasError && hasBeenAttempted)) && (
                <div
                  className={`w-10 h-7 absolute bottom-0 right-0 flex items-center justify-center rounded-tl-lg rounded-br-[18px] ${
                    hasError && hasBeenAttempted ? "bg-brand-terracotta" : "bg-brand-olive-alpha-40"
                  }`}
                >
                  {hasError && hasBeenAttempted ? (
                    <AlertCircle
                      className="w-5 h-5 text-white pointer-events-none"
                      aria-hidden="true"
                    />
                  ) : (
                    <Check className="w-5 h-5 text-white pointer-events-none" aria-hidden="true" />
                  )}
                </div>
              )}

              {/* Accordion Header */}
              <button
                onClick={() => toggleItem(item.id)}
                onKeyDown={e => handleKeyDown(e, item.id)}
                aria-expanded={isOpen}
                aria-controls={`accordion-content-${item.id}`}
                className={`w-full flex items-center justify-between rounded-[20px] px-5 text-left ${
                  isOpen ? "pt-5" : "p-5 pb-4"
                }`}
              >
                <div className="flex flex-col items-start gap-1.25">
                  {/* Title line - prioritize userTitle from Info, then displayName, then title */}
                  <span className={"text-[18px] leading-6.25 font-semibold"}>
                    {!isOpen && item.userTitle
                      ? item.userTitle
                      : isComplete && !isOpen && item.displayName
                        ? item.displayName
                        : item.title}
                  </span>

                  {/* Summary, error, or user previews - Show when complete/error and closed */}
                  {!isOpen && (isComplete || (hasError && hasBeenAttempted)) && (
                    <>
                      {/* For Admin & Roles: Show comma-separated names with count */}
                      {item.id === "users-roles" &&
                      item.userPreviews &&
                      item.userPreviews.length > 0 ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] leading-5">
                            {item.userPreviews.map(user => user.name || user.email).join(", ")}
                            {item.totalCount && item.totalCount > item.userPreviews.length && (
                              <span className="text-[12px] text-primary ">
                                {" "}
                                +{item.totalCount - item.userPreviews.length} more
                              </span>
                            )}
                          </span>
                        </div>
                      ) : hasError && hasBeenAttempted ? (
                        /* Show error message when hasError is true and section has been attempted */
                        item.error && (
                          <p className="text-[13px] leading-5 flex items-center gap-2.5 text-[var(--color-brand-terracotta)]">
                            {item.error}
                          </p>
                        )
                      ) : (
                        /* For other sections: Show summary text */
                        item.summary && (
                          <p className="text-[13px] leading-5 flex items-center gap-2.5">
                            {/* <span className="bg-[#C86757] rounded-[5px] px-1.5 py-0.5">$15 </span> */}
                            {item.summary}
                          </p>
                        )
                      )}
                    </>
                  )}
                </div>
                {!isOpen && (
                  <Pencil
                    className={`w-5 h-5 flex-shrink-0 cursor-pointer absolute right-5 top-4`}
                    aria-hidden="true"
                  />
                )}
              </button>

              {/* Accordion Content */}
              <div
                id={`accordion-content-${item.id}`}
                aria-labelledby={`accordion-header-${item.id}`}
                className={`transition-all duration-200 ease-in-out ${
                  isOpen ? "opacity-100 max-h-none" : "max-h-0 opacity-0 overflow-hidden"
                }`}
              >
                <div className="px-5 pb-5 pt-1.25">
                  <div className="text-sm">
                    {item.content}
                    {showNextButton && (
                      <div
                        className={`${item.title === "Organizers" ? "mt-5" : "mt-6"} flex justify-end`}
                      >
                        <Button
                          onClick={() => handleNext(index)}
                          className="uppercase text-sm px-23 h-12.5 w-[220px]"
                          variant="primary"
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  },
)

Accordion.displayName = "Accordion"
